<template>
  <div class="navbar-container">
    <!-- Phone bar: ring gauges (fill = usage of cap) and bare resource
         totals. Every item here is a BUTTON — a tap opens the list or
         drawer behind it, never a tooltip; income lives in the resource
         drawer and the caps in each list's header. Desktop bar below is
         untouched. -->
    <div
      v-if="isMobileView"
      class="navbar bottom is-mobile">
      <div class="mobile-bottombar">
        <div
          class="mobile-bb-btn"
          @click="togglePanel('empire')">
          <svgicon class="icon" name="empire" />
        </div>

        <div
          class="mobile-gauge-press"
          @pointerdown="gaugePressStart('systems', $event)"
          @pointerup="gaugePressEnd('systems', $event)"
          @pointercancel="gaugePressCancel"
          @contextmenu.prevent>
          <mobile-gauge
            :value="ownSystems.length"
            :max="player.max_systems.value"
            glyph="system"
            :theme="theme" />
        </div>
        <div
          class="mobile-gauge-press"
          @pointerdown="gaugePressStart('dominions', $event)"
          @pointerup="gaugePressEnd('dominions', $event)"
          @pointercancel="gaugePressCancel"
          @contextmenu.prevent>
          <mobile-gauge
            :value="ownDominions.length"
            :max="player.max_dominions.value"
            glyph="dominion"
            :theme="theme" />
        </div>

        <span class="mobile-bb-divider"></span>

        <div
          ref="nesGauge"
          class="mobile-gauge-press"
          @pointerdown="gaugePressStart('nes', $event)"
          @pointerup="gaugePressEnd('nes', $event)"
          @pointercancel="gaugePressCancel"
          @contextmenu.prevent>
          <mobile-tri-gauge :segments="nesSegments" />
        </div>

        <div
          class="mobile-bb-minipanel"
          @click="toggleMiniPanel('doctrine')">
          <svgicon name="doctrine/frame_doctrine" />
          {{ $t('navbar.bottombar.lexes') }}
        </div>
        <div
          class="mobile-bb-minipanel"
          @click="toggleMiniPanel('patent')">
          <svgicon name="patent/frame_patent" />
          {{ $t('navbar.bottombar.patents_short') }}
        </div>

        <span class="mobile-bb-spacer"></span>

        <div
          v-for="res in ['credit', 'technology', 'ideology']"
          :key="`res-${res}`"
          class="mobile-bb-resource"
          :class="{ 'is-active': resourceDrawer === res }"
          @click="openResourceDrawer(res)">
          <svgicon :name="`resource/${res}`" />
          {{ compactNumber(player[res].value) }}
        </div>

        <div
          class="mobile-bb-btn"
          @click="togglePanel('operations')">
          <svgicon class="icon" name="operation" />
        </div>
      </div>
    </div>

    <!-- The desktop bar must IMMEDIATELY follow the mobile bar's div:
         v-else detaches from its v-if if any element sits between
         them, and the desktop bar then renders unconditionally. -->
    <div
      v-else
      class="navbar bottom">
      <div class="navbar-left">
        <!-- TODO: should be a component -->
        <div class="navbar-main-button">
          <div class="navbar-main-button-toolbox">
            <div
              class="button"
              v-if="ownSystems.length > 0"
              v-press
              :aria-expanded="String(isSystemListOpen)"
              :aria-label="$t('navbar.list_panel.toggle_systems')"
              @click="toggleSystemList">
              <template v-if="isSystemListOpen">
                <svgicon class="icon" name="caret-down" />
              </template>
              <template v-else>
                <svgicon class="icon" name="caret-up" />
              </template>
            </div>
          </div>

          <div
            @click="togglePanel('empire')"
            class="navbar-main-button-icon">
            <svgicon class="icon" name="empire" />
          </div>
        </div>

        <div class="navbar-group-buttons left">
          <div
            v-if="tutorialStep === 6"
            class="tutorial-pointer is-technology is-bottom">
          </div>
          <div
            v-if="tutorialStep === 10"
            class="tutorial-pointer is-ideology is-bottom">
          </div>
          <v-popover trigger="hover">
            <navbar-maxed-value
              :label="$t('navbar.bottombar.systems')"
              :value="player.stellar_systems.length"
              :maximum="player.max_systems.value" />
            <resource-detail
              slot="popover"
              :title="$t('navbar.bottombar.systems_limit')"
              :precision="0"
              :value="player.max_systems.value"
              :details="player.max_systems.details" />
          </v-popover>

          <v-popover trigger="hover">
            <navbar-maxed-value
              :label="$t('navbar.bottombar.dominions')"
              :value="player.dominions.length"
              :maximum="player.max_dominions.value" />
            <resource-detail
              slot="popover"
              :title="$t('navbar.bottombar.dominions_limit')"
              :precision="0"
              :value="player.max_dominions.value"
              :details="player.max_dominions.details" />
          </v-popover>

          <hover-popover
            @mouseenter.native="setHoveredResource('credit')"
            @mouseleave.native="setHoveredResource(null)">
            <navbar-dynamic-value
              icon="resource/credit"
              :initial="player.credit" />
            <resource-detail
              slot="popover"
              :income="true"
              :title="$t('data.bonus_pipeline_in.player_credit.name')"
              help="credit"
              :description="$t(`resource-description.credit`)"
              :value="player.credit.change"
              :rates="resourceRates(player.credit)"
              :totals="resourceTotals(player.credit)"
              :details="player.credit.details" />
          </hover-popover>

          <hover-popover
            @mouseenter.native="setHoveredResource('technology')"
            @mouseleave.native="setHoveredResource(null)">
            <navbar-dynamic-value
              icon="resource/technology"
              :initial="player.technology" />
            <resource-detail
              slot="popover"
              :income="true"
              :title="$t('data.bonus_pipeline_in.player_technology.name')"
              help="technology"
              :description="$t(`resource-description.technology`)"
              :value="player.technology.change"
              :rates="resourceRates(player.technology)"
              :totals="resourceTotals(player.technology)"
              :details="player.technology.details" />
          </hover-popover>

          <hover-popover
            @mouseenter.native="setHoveredResource('ideology')"
            @mouseleave.native="setHoveredResource(null)">
            <navbar-dynamic-value
              icon="resource/ideology"
              :initial="player.ideology" />
            <resource-detail
              slot="popover"
              :income="true"
              :title="$t('data.bonus_pipeline_in.player_ideology.name')"
              help="ideology"
              :description="$t(`resource-description.ideology`)"
              :value="player.ideology.change"
              :rates="resourceRates(player.ideology)"
              :totals="resourceTotals(player.ideology)"
              :details="player.ideology.details" />
          </hover-popover>

          <!-- Copy shortcuts (also on the C key): hidden by CSS below the
               width where the bar runs out of room. The text button copies
               the Discord flavor whenever the account has Discord linked,
               regardless of the profile's default copy mode. -->
          <div class="navbar-copy-buttons">
            <div
              class="navbar-copy-button"
              v-tooltip="$t('navbar.bottombar.copy_text')"
              @click="copyResourcesAs(hasDiscordLinked ? 'discord' : 'plaintext')">
              <svgicon name="share" />
            </div>
            <div
              class="navbar-copy-button"
              v-tooltip="$t('navbar.bottombar.copy_picture')"
              @click="copyResourcesAs('picture')">
              <svgicon name="square" />
            </div>
          </div>
        </div>
      </div>

      <div class="navbar-center">
        <div
          v-if="tutorialStep === 7"
          class="tutorial-pointer is-patent is-bottom">
        </div>
        <div
          v-if="tutorialStep === 11"
          class="tutorial-pointer is-doctrine is-bottom">
        </div>

        <div
          @click="switchSystem('prev')"
          v-if="ownSystems.length > 1"
          v-tooltip="$t('navbar.bottombar.previous_system')"
          class="mini-panel-switcher left">
          <svgicon name="caret-left" />
        </div>

        <div
          @click="toggleMiniPanel('patent')"
          :class="{ 'active': activeMiniPanel.name === 'patent' }"
          v-tooltip="$t('navbar.bottombar.patents')"
          class="mini-panel-button left">
          <svgicon name="patent/frame_patent" />
        </div>

        <navbar-player />

        <div
          @click="toggleMiniPanel('doctrine')"
          :class="{ 'active': activeMiniPanel.name === 'doctrine' }"
          v-tooltip="$t('navbar.bottombar.lexes')"
          class="mini-panel-button right">
          <svgicon name="doctrine/frame_doctrine" />
        </div>

        <div
          @click="switchSystem('next')"
          v-if="ownSystems.length > 1"
          v-tooltip="$t('navbar.bottombar.next_system')"
          class="mini-panel-switcher right">
          <svgicon name="caret-right" />
        </div>
      </div>

      <div class="navbar-right">
        <div class="navbar-group-buttons right">
          <div
            class="navbar-deploy-button"
            @click="toggleMiniPanel('character-deck')">
            <strong>{{ $t('navbar.bottombar.agents') }}</strong>
            {{ $t('navbar.bottombar.n_available', {n: playerDeck.length}) }}
          </div>

          <v-popover
            v-for="type in characterData"
            :key="type.key"
            trigger="hover">
            <navbar-maxed-value
              :label="$tc(`data.character.${type.key}.name`, type.activeNumber)"
              :value="type.activeNumber"
              :maximum="type.maxNumber" />
            <resource-detail
              slot="popover"
              :title="$t('navbar.bottombar.character_type_limit', {characterType: $tc(`data.character.${type.key}.name`, 2)})"
              :precision="0"
              :value="player[charactersBonusName[type.key]].value"
              :details="player[charactersBonusName[type.key]].details" />
          </v-popover>
        </div>

        <!-- TODO: should be a component -->
        <div class="navbar-main-button">
          <div class="navbar-main-button-toolbox">
            <div
              class="button"
              v-if="listedCharacters.length > 0"
              v-press
              :aria-expanded="String(isActiveCharacterListOpen)"
              :aria-label="$t('navbar.list_panel.toggle_agents')"
              @click="toggleActiveCharacterList">
              <template v-if="isActiveCharacterListOpen">
                <svgicon class="icon" name="caret-down" />
              </template>
              <template v-else>
                <svgicon class="icon" name="caret-up" />
              </template>
            </div>
          </div>

          <div
            @click="togglePanel('operations')"
            class="navbar-main-button-icon">
            <svgicon class="icon" name="operation" />
          </div>
        </div>
      </div>
    </div>

    <agents-list-panel
      v-if="!isMobileView"
      v-show="isActiveCharacterListOpen && listedCharacters.length > 0 && !selection" />

    <systems-list-panel
      v-if="!isMobileView"
      v-show="!selectedSystem && isSystemListOpen" />

    <!-- Long-press radial: N / E / S bubbles over the tri-gauge;
         release (or tap) on one lists that class's agents. Lives
         outside .navbar.bottom — its overflow clipping would swallow
         anything drawn above the 44px bar. -->
    <div
      v-if="radialOpen"
      class="mobile-radial"
      :style="{ left: `${radialX}px` }">
      <div
        v-for="(type, i) in characterData"
        :key="`radial-${type.key}`"
        class="mobile-radial-item"
        :class="`is-pos-${i}`"
        :data-agent-type="type.key"
        @click="openAgentsModal(type.key)">
        <span class="letter">{{ $tc(`data.character.${type.key}.name`, 1).charAt(0) }}</span>
        <span class="count">{{ type.activeNumber }}/{{ type.maxNumber }}</span>
      </div>
    </div>

    <mobile-resource-drawer
      v-if="resourceDrawer"
      :initial="resourceDrawer"
      @close="resourceDrawer = null" />

    <mobile-list-modal
      v-if="activeListModal"
      :title="activeListModal.title"
      :items="activeListModal.items"
      @select="onModalSelect"
      @close="activeListModal = null" />

    <div
      class="mini-panels-container"
      ref="miniPanelsContainer"
      @click.self="closeMiniPanel">
      <character-deck-mini-panel
        v-show="activeMiniPanel.name === 'character-deck'"
        :height="activeMiniPanel.height"
        @close="closeMiniPanel" />
      <patent-mini-panel
        v-show="activeMiniPanel.name === 'patent'"
        :height="activeMiniPanel.height"
        @close="closeMiniPanel" />
      <doctrine-mini-panel
        v-show="activeMiniPanel.name === 'doctrine'"
        :active-panel="activeMiniPanel.name"
        :height="activeMiniPanel.height"
        @close="closeMiniPanel" />
      <faction-tree-mini-panel
        v-if="['faction-patent', 'faction-lex'].includes(activeMiniPanel.name)"
        :default-tab="activeMiniPanel.name === 'faction-patent' ? 'patent' : 'lex'"
        :height="activeMiniPanel.height"
        @close="closeMiniPanel" />
    </div>
  </div>
</template>

<script>
import { TimelineLite, Expo } from 'gsap';
import HoverPopover from '@/game/components/generic/HoverPopover.vue';

import viewport from '@/utils/viewport';
import ResourceRatesMixin from '@/game/mixins/ResourceRatesMixin';
import { copyResourcesForVm } from '@/game/resource-copy';

import NavbarDynamicValue from '@/game/components/navbar/NavbarDynamicValue.vue';
import MobileGauge from '@/game/components/navbar/MobileGauge.vue';
import MobileTriGauge from '@/game/components/navbar/MobileTriGauge.vue';
import MobileListModal from '@/game/components/navbar/MobileListModal.vue';
import MobileResourceDrawer from '@/game/components/navbar/MobileResourceDrawer.vue';
import NavbarMaxedValue from '@/game/components/navbar/NavbarMaxedValue.vue';
import AgentsListPanel from '@/game/components/navbar/AgentsListPanel.vue';
import SystemsListPanel from '@/game/components/navbar/SystemsListPanel.vue';

import NavbarPlayer from '@/game/components/navbar/NavbarPlayer.vue';
import ResourceDetail from '@/game/components/generic/ResourceDetail.vue';

import CharacterDeckMiniPanel from '@/game/components/mini-panel/CharacterDeckMiniPanel.vue';
import PatentMiniPanel from '@/game/components/mini-panel/PatentMiniPanel.vue';
import DoctrineMiniPanel from '@/game/components/mini-panel/DoctrineMiniPanel.vue';
import FactionTreeMiniPanel from '@/game/components/mini-panel/FactionTreeMiniPanel.vue';

export default {
  name: 'bottombar',
  mixins: [ResourceRatesMixin],
  data() {
    return {
      activeMiniPanel: { name: '' },
      isMiniPanelOpen: false,
      miniPanels: [
        { name: 'character-deck', height: 480 },
        // The tree panels dock a detail card top-right: card, action
        // button, then the "right-click to hold" hint, which needs these
        // 30px to clear the navbar.
        { name: 'patent', height: 510 },
        // The doctrine cards stack two action rows (Buy / Buy and
        // Activate) below the 418px card, so this panel needs the extra
        // height to keep the second row clickable above the navbar.
        { name: 'doctrine', height: 545 },
        { name: 'faction-patent', height: 510 },
        { name: 'faction-lex', height: 510 },
      ],
      isActiveCharacterListOpen: true,
      isSystemListOpen: true,
      // Mobile long-press state: the radial N/E/S picker over the
      // tri-gauge, and the full-screen list modals that replace the
      // desktop pull-up docks.
      radialOpen: false,
      radialX: 0,
      activeListModal: null,
      // Which resource the drawer opened on (null = closed). The bar is
      // too short for the desktop hover tooltips, so a tap raises a
      // bottom sheet instead — see MobileResourceDrawer.
      resourceDrawer: null,
      pressTimer: null,
      pressFiredLong: false,
      characterDeck: false,
      charactersBonusName: {
        admiral: 'max_admirals',
        spy: 'max_spies',
        speaker: 'max_speakers',
      },
    };
  },
  computed: {
    isMobileView() { return viewport.isMobile; },
    tutorialStep() { return this.$store.state.game.tutorialStep; },
    theme() { return this.$store.getters['game/theme']; },
    view() { return this.$store.state.game.view; },
    player() { return this.$store.state.game.player; },
    ownSystems() { return this.player.stellar_systems; },
    // Shown to the owner in the /account payload; presence == linked.
    hasDiscordLinked() { return !!this.$store.state.portal.account.discord_id; },
    ownDominions() { return this.player.dominions; },
    selection() { return this.$store.state.game.selectedCharacter; },
    selectedSystem() { return this.$store.state.game.selectedSystem; },
    onBoardCharacters() { return this.player.characters.filter((p) => p.status === 'on_board'); },
    // what the agents list shows: the agents on the board and in training
    listedCharacters() { return this.player.characters.filter((p) => ['on_board', 'student'].includes(p.status)); },
    playerDeck() { return this.$store.state.game.player.character_deck; },
    nesSegments() {
      return this.characterData.map((type) => ({
        letter: this.$tc(`data.character.${type.key}.name`, 1).charAt(0),
        value: type.activeNumber,
        max: type.maxNumber,
      }));
    },
    characterData() {
      return this.$store.state.game.data.character.map((data) => {
        const activeNumber = this.player.characters.filter((c) => c.type === data.key).length;
        const maxNumber = this.player[this.charactersBonusName[data.key]].value;

        return { ...data, ...{ activeNumber, maxNumber } };
      });
    },
  },
  methods: {
    // --- mobile long-press gauges -----------------------------------
    // Tap the tri-gauge: toggles the radial (then tap a letter).
    // Long-press: radial opens mid-hold, releasing over a letter picks
    // it — the gesture the user described. Systems/dominions gauges
    // long-press straight into their list modal.
    gaugePressStart(kind, event) {
      this.pressFiredLong = false;
      clearTimeout(this.pressTimer);
      this.pressTimer = setTimeout(() => {
        this.pressFiredLong = true;
        if (kind === 'nes') {
          this.openRadial(event);
        }
      }, 450);
    },
    gaugePressEnd(kind, event) {
      clearTimeout(this.pressTimer);

      if (kind !== 'nes') {
        // Systems/dominions: a plain tap opens the list (long-press
        // proved unreliable on real devices).
        this.openSystemsModal(kind === 'systems' ? 'systems' : 'dominions');
        return;
      }

      if (!this.pressFiredLong) {
        // plain tap: toggle the radial
        if (this.radialOpen) {
          this.radialOpen = false;
        } else {
          this.openRadial(event);
        }
        return;
      }

      // long-press release: select whichever bubble the finger is on
      const el = document.elementFromPoint(event.clientX, event.clientY);
      const bubble = el && el.closest('.mobile-radial-item');
      if (bubble) {
        this.openAgentsModal(bubble.dataset.agentType);
      }
      // released elsewhere: keep the radial open so a follow-up tap
      // can still choose (dismiss by tapping the gauge again)
    },
    gaugePressCancel() {
      clearTimeout(this.pressTimer);
    },
    openRadial(event) {
      const rect = (this.$refs.nesGauge || event.target).getBoundingClientRect
        ? (this.$refs.nesGauge || event.target).getBoundingClientRect()
        : { left: event.clientX, width: 0 };
      this.radialX = Math.round(rect.left + rect.width / 2);
      this.radialOpen = true;
    },
    openAgentsModal(typeKey) {
      this.radialOpen = false;
      if (!typeKey) return;
      const items = this.player.characters
        .filter((c) => c.type === typeKey)
        .map((c) => ({
          id: c.id,
          icon: `agent/${c.type}`,
          label: c.name,
          sub: this.$tc(`data.character.${c.type}.name`, 1),
          right: `lv ${c.level}`,
          kind: 'character',
        }));
      this.activeListModal = {
        title: this.$tc(`data.character.${typeKey}.name`, 2),
        items,
      };
    },
    openSystemsModal(which) {
      const source = which === 'systems' ? this.ownSystems : this.ownDominions;
      // The gauges only show the owned count; the cap lives here.
      const max = which === 'systems' ? this.player.max_systems.value : this.player.max_dominions.value;
      this.activeListModal = {
        title: `${this.$t(`navbar.bottombar.${which}`)} — ${source.length} / ${max}`,
        items: source.map((s) => ({
          id: s.id,
          icon: null,
          label: s.name,
          sub: `${Math.trunc(s.position.x)}:${Math.trunc(s.position.y)}`,
          kind: 'system',
        })),
      };
    },
    openResourceDrawer(res) {
      this.resourceDrawer = this.resourceDrawer === res ? null : res;
    },
    onModalSelect(item) {
      this.activeListModal = null;
      if (item.kind === 'character') {
        this.$store.dispatch('game/selectCharacter', { vm: this, id: item.id });
      } else if (item.kind === 'system') {
        this.$store.dispatch('game/openSystem', { vm: this, id: item.id });
      }
    },

    // Bar space on a phone is too tight for full figures — 300,018
    // reads as 300k; the exact figure is in the resource drawer.
    compactNumber(value) {
      const n = Math.floor(value);
      if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
      if (Math.abs(n) >= 10_000) return `${Math.round(n / 1000)}k`;
      return `${n}`;
    },
    toggleActiveCharacterList() {
      this.isActiveCharacterListOpen = !this.isActiveCharacterListOpen;
    },
    toggleSystemList() {
      this.isSystemListOpen = !this.isSystemListOpen;
    },
    toggleMiniPanel(name) {
      if (this.isMiniPanelOpen && this.activeMiniPanel.name === name) {
        this.closeMiniPanel();
      } else {
        this.openMiniPanel(name);
      }
    },
    openMiniPanel(name) {
      this.$root.$emit('closePanel');
      this.$root.$emit('closeTopMiniPanel');

      this.animateCloseMiniPanelContainer().then(() => {
        this.animateOpenMiniPanelContainer(name);
      });
    },
    closeMiniPanel() {
      this.$store.commit('game/clearAssignment');

      this.animateCloseMiniPanelContainer().then(() => {
        this.isMiniPanelOpen = false;
        this.activeMiniPanel = { name: '' };
      });
    },
    switchSystem(mode) {
      let nextSystemId;

      if (mode === 'first') {
        nextSystemId = this.ownSystems[0].id;
      } else {
        const i = this.selectedSystem
          ? this.ownSystems.findIndex((s) => s.id === this.selectedSystem.id)
          : -1;

        nextSystemId = mode === 'prev'
          ? this.ownSystems[(i + this.ownSystems.length - 1) % this.ownSystems.length].id
          : this.ownSystems[(i + 1) % this.ownSystems.length].id;
      }

      this.$store.dispatch('game/openSystem', { vm: this, id: nextSystemId });
    },
    animateOpenMiniPanelContainer(name) {
      return new Promise((resolve) => {
        this.$ambiance.sound('mini-panel-open');

        this.$refs.miniPanelsContainer.style.display = 'flex';
        this.activeMiniPanel = this.miniPanels.find((p) => p.name === name);
        this.isMiniPanelOpen = true;

        new TimelineLite({
          onComplete() { resolve(); },
        }).set(this.$refs.miniPanelsContainer, { bottom: `-${this.activeMiniPanel.height}px` })
          .to(this.$refs.miniPanelsContainer, { bottom: '52px', ease: Expo.easeOut, duration: 0.8 }, 0);
      });
    },
    animateCloseMiniPanelContainer() {
      if (!this.isMiniPanelOpen) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        this.$ambiance.sound('mini-panel-close');

        const self = this;

        if (!this.isMiniPanelOpen) {
          resolve();
        } else {
          const position = `-${this.activeMiniPanel.height}px`;

          new TimelineLite({
            onComplete() {
              self.$refs.miniPanelsContainer.style.display = 'none';
              resolve();
            },
          }).to(this.$refs.miniPanelsContainer, { bottom: position, ease: Expo.linear, duration: 0.4 }, 0);
        }
      });
    },
    togglePanel(name) {
      this.$root.$emit('togglePanel', name);
    },
    setHoveredResource(name) {
      this.$root.$emit('hoveredResource', name);
    },
    async copyResourcesAs(mode) {
      await copyResourcesForVm(this, mode);
    },
  },
  mounted() {
    // Bound refs so beforeDestroy can $off — $root outlives this
    // component, so anonymous closures stack across game re-entries.
    this.onOpenMiniPanel = (name) => { this.openMiniPanel(name); };
    this.onCloseMiniPanel = () => { this.closeMiniPanel(); };
    this.onSwitchSystem = (mode) => { this.switchSystem(mode); };
    this.$root.$on('openBottomMiniPanel', this.onOpenMiniPanel);
    this.$root.$on('closeBottomMiniPanel', this.onCloseMiniPanel);
    this.$root.$on('switchSystem', this.onSwitchSystem);
  },
  beforeDestroy() {
    this.$root.$off('openBottomMiniPanel', this.onOpenMiniPanel);
    this.$root.$off('closeBottomMiniPanel', this.onCloseMiniPanel);
    this.$root.$off('switchSystem', this.onSwitchSystem);
  },
  components: {
    HoverPopover,
    MobileGauge,
    MobileTriGauge,
    MobileListModal,
    MobileResourceDrawer,
    NavbarDynamicValue,
    NavbarMaxedValue,
    AgentsListPanel,
    SystemsListPanel,
    NavbarPlayer,
    ResourceDetail,
    CharacterDeckMiniPanel,
    PatentMiniPanel,
    DoctrineMiniPanel,
    FactionTreeMiniPanel,
  },
};
</script>
